import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export type AppointmentLanguage = 'ar' | 'en';

export type AppointmentWallTime = {
  dateKey: string;
  minutes: number;
};

export const appointmentWallTimeToIso = (
  dateKey: string,
  minutes: number,
  timezone: string
): string => {
  const wholeMinutes = Math.trunc(minutes);
  const hour = Math.floor(wholeMinutes / 60);
  const minute = wholeMinutes % 60;
  const wallTime = `${dateKey} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  return dayjs.tz(wallTime, timezone).toISOString();
};

export const appointmentInstantToWallTime = (
  value: string | Date,
  timezone: string
): AppointmentWallTime => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { dateKey: '', minutes: 0 };

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const read = (type: string) => parts.find(part => part.type === type)?.value || '';
  return {
    dateKey: `${read('year')}-${read('month')}-${read('day')}`,
    minutes: Number(read('hour')) * 60 + Number(read('minute')),
  };
};

const localizedPeriod = (period: string, language: AppointmentLanguage): string => {
  const isPm = period.toUpperCase() === 'PM';
  if (language === 'ar') return isPm ? 'م' : 'ص';
  return isPm ? 'PM' : 'AM';
};

export const formatAppointmentMinutes = (
  minutes: number,
  language: AppointmentLanguage
): string => {
  const normalized = ((Math.trunc(minutes) % 1440) + 1440) % 1440;
  const hour24 = Math.floor(normalized / 60);
  const minute = normalized % 60;
  const hour12 = hour24 % 12 || 12;
  const period = localizedPeriod(hour24 < 12 ? 'AM' : 'PM', language);
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
};

export const formatAppointmentInstant = (
  value: string | Date,
  language: AppointmentLanguage,
  timezone: string
): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const read = (type: string) => parts.find(part => part.type === type)?.value || '';
  return `${read('hour')}:${read('minute')} ${localizedPeriod(read('dayPeriod'), language)}`;
};

export const formatAppointmentDateTime = (
  value: string | Date,
  language: AppointmentLanguage,
  timezone: string
): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const dateLabel = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
  return `${dateLabel} ${formatAppointmentInstant(date, language, timezone)}`;
};
